//================================================================
//  Component: Manage Approval
//================================================================

//  Purpose: Sequence of approval management pages for Mobile view

//  Properties:
//    - requestDoc = {Object, contains approval details}

//  Example:
//  <ManageApproval 
//    requestDoc={requestDoc}
//  ></ManageApproval> 

//================================================================


//Libraries
import React, { useState, useContext, useReducer } from 'react';
import { Link } from 'react-router-dom';

//Contexts
import { GetUser } from '../../Library/GlobalContexts';

//Components
import UserSearchNative from '../UserSearchNative/UserSearchNative';

//Functions
import WriteDocument from '../../Library/WriteDocument';
import AddRemoveDocumentArray from '../../Library/AddRemoveDocumentArray';

//Images
import ArrowIcon from '../../Components/Images/Icon_LeftArrow_Black.svg';
import TickIcon from '../../Components/Images/Icon_Approved_Green.svg';
import ErrorIcon from '../../Components/Images/Icon_Error_Red.svg'

export default function ManageApproval({
  requestDoc
}) {

  //------------------------------------------------------
  //  useContexts
  //------------------------------------------------------

  const getUser = useContext(GetUser);

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

  // Stages for different pages > 'pre-selection', 'selection', 'post-selection', 'error'
  const [stage, setStage] = useState('pre-selection');

  // For changing request status dropdown > 'Approve', 'Reassign', 'Reject'
  const [requestStatus, setRequestStatus] = useState('');

  // Some status changes require an additional comment
  const [changeStatusComment, setChangeStatusComment] = useState('');


  //------------------------------------------------------
  //  useReducer
  //------------------------------------------------------

  // Used to store the search data
  const [searchData, setSearchData] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      'newUserInput': '', // email of the selected user
      'newUserName': '', // fullname of the selected user
      'newGivenname': '', // givenname of the selected user
      'newSurname': '', // surname of the selected user
      'newJobTitle': '', // jobtitle of the selected user
      'searchResults': '',
      'queryMessage': '',
    }
  );

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  // Disables the 'Proceed' button if conditions aren't met
  function isSubmitDisabled() {

    if (getUser?.emailaddress !== requestDoc?.assignedto?.email) return true;

    if (requestStatus === 'Approve') {
      return false;
    }

    if (requestStatus === 'Reassign') {
      return searchData?.newUserInput && changeStatusComment.length > 0 ? false : true;
    }

    if (requestStatus === 'Reject') {
      return changeStatusComment.length > 0 ? false : true;
    }

    return true;

  }

  // Submits form
  function handleSubmit() {

    if (requestStatus === 'Approve') {

      const newActivityFeed = [...requestDoc.activityfeed, {
        'action': 'approved',
        'actionedby': {
          'email': getUser?.emailaddress,
          'givenname': getUser?.givenname,
          'surname': getUser?.surname,
        },
        'activitydate': new Date(),
        'comments': changeStatusComment
      }]

      const docObject = {
        'status': 'Completed',
        'activityfeed': newActivityFeed,
      }

      return WriteDocument('requests', requestDoc?.requestid, docObject, true)
        .then(() => {
          setRequestStatus('');
          setChangeStatusComment('');
          setStage('post-selection');
        })
        .catch((err) => {
          console.log(err);
          setStage('error');
        })
    }

    if (requestStatus === 'Reassign') {

      const newActivityFeed = [...requestDoc.activityfeed, {
        'action': 'unassigned',
        'actionedby': {
          'email': getUser.emailaddress,
          'givenname': getUser.givenname,
          'surname': getUser.surname,
        },
        'activitydate': new Date(),
        'comments': changeStatusComment
      },
      {
        'action': 'assigned',
        'actionedby': {
          'email': searchData.newUserInput,
          'givenname': searchData.newGivenname,
          'surname': searchData.newSurname,
        },
        'activitydate': new Date(),
        'comments': changeStatusComment
      }]

      const docObject = {
        'activityfeed': newActivityFeed,
        'assignedto': {
          'email': searchData?.newUserInput,
          'givenname': searchData?.newGivenname,
          'surname': searchData?.newSurname,
        },
        'lastmodifieddate': new Date(),
        'comments': changeStatusComment,
      }

      return WriteDocument('requests', requestDoc?.requestid, docObject, true)
        .then(() => {

          setRequestStatus('');
          setChangeStatusComment('');

          setSearchData({
            'newUserInput': '', // email of the selected user
            'newUserName': '', // fullname of the selected user
            'newGivenname': '', // givenname of the selected user
            'newSurname': '', // surname of the selected user
            'newJobTitle': '', // jobtitle of the selected user
            'searchResults': '',
            'queryMessage': '',
          });

          setStage('post-selection');

        })
        .catch((err) => {

          console.log(err);
          setStage('error')

        })
    }

    if (requestStatus === 'Reject') {

      // Update the activity feed (array field)
      const newActivityFeed = {
        'action': 'rejected',
        'actionedby': {
          'email': getUser?.emailaddress,
          'givenname': getUser?.givenname,
          'surname': getUser?.surname,
        },
        'activitydate': new Date(),
        'comments': changeStatusComment
      }

      const updateActivityFeedPromise = AddRemoveDocumentArray('requests', requestDoc?.requestid, 'activityfeed', newActivityFeed, 'add', newActivityFeed.actionedby);

      // Update status and other fields
      const docObject = {
        'status': 'Rejected',
        'lastmodifiedby': {
          'email': getUser.emailaddress,
          'givenname': getUser.givenname,
          'surname': getUser.surname,
        },
        'lastmodifieddate': new Date(),
        'rejectionreason': changeStatusComment,
      }

      const updateStatusPromise = WriteDocument('requests', requestDoc?.requestid, docObject, true);

      return Promise.all([updateActivityFeedPromise, updateStatusPromise])
        .then(() => {

          setRequestStatus('');
          setChangeStatusComment('');
          setStage('post-selection');

        })
        .catch((err) => {

          console.log(err);
          setStage('error')

        })
    }

  }

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  // Returns nothing if request has already been approved/rejected in the past or user is not authorised
  if (requestDoc?.status !== 'In Progress' && requestDoc?.status !== 'For Review' && requestDoc?.assignedto !== getUser?.emailaddress && stage !== 'post-selection') return;

  return (
    <>

      {/* =========================================================== */}
      {/* PRE-SELECTION STAGE: ONLY SHOW BUTTON                       */}
      {/* =========================================================== */}

      <div className='fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-300 w-full py-4 px-4 z-10' hidden={stage !== 'pre-selection' || requestDoc?.assignedto?.email !== getUser?.emailaddress}>
        <button
          className='w-full bg-black text-white py-2 rounded-md font-medium'
          onClick={() => {
            setStage('selection');
            setRequestStatus('');
          }}
        >
          Manage Approval
        </button>
      </div>

      {/* =========================================================== */}
      {/* SELECTION STAGE                                             */}
      {/* =========================================================== */}

      <div className='absolute flex flex-col h-[calc(100vh-70px)] w-full top-[70px] z-10 bg-white' hidden={stage !== 'selection'}>

        <div className='flex flex-col h-full justify-between'>

          <div>

            {/* HEADER */}
            <div className='flex flex-row p-3 border-b-[1px] border-neutral-300'>
              {/* Back: Returns to previous screen, does not clear Text field */}
              <img src={ArrowIcon} alt='Back' className='h-6' onClick={() => { setStage('pre-selection') }}></img>
              <h5 className='m-auto'>Manage Approval</h5>
            </div>

            {/* BODY */}
            <div className='flex flex-col gap-4 p-3'>
              <label className='font-semibold'>Change status:</label>
              <select
                className='Input-Field-Select'
                value={requestStatus}
                disabled={requestDoc?.status !== 'In Progress' && requestDoc?.status !== 'For Review'}
                onChange={(e) => setRequestStatus(e.target.value)}
              >
                {/* If Completed, show as 'Approved', otherwise show 'Rejected'. If pending approval, show 'Select' */}
                <option hidden value=''>Select</option>
                <option value='Approve'>Approve</option>
                <option value='Reassign'>Reassign</option>
                <option className='text-[#DE0000]' value='Reject'>Reject</option>
              </select>

              <div hidden={requestStatus !== 'Reassign'} className='flex flex-col gap-2'>

                <label className='font-semibold'>
                  Reassign request to: <span className='text-[#C4314B]'>*</span>
                </label>

                <UserSearchNative
                  selectedUser={searchData}
                  setSelectedUser={setSearchData}
                  errorMessage={undefined}
                ></UserSearchNative>

              </div>

              <div hidden={requestStatus.length === 0} className='flex flex-col gap-2'>

                {/* Change help text based off approval status */}
                {
                  requestStatus === 'Approve' ?

                    <label className='font-semibold'>
                      Comment
                    </label>

                    :

                    <div className='flex flex-col gap-2'>
                      <label className='font-semibold'>
                        Comment <span className='text-[#C4314B]'>*</span>
                      </label>
                      <i className='pb-2' hidden={requestStatus !== 'Reject'}>Please enter a reason for your decision to reject the request.</i>

                    </div>

                }

                <textarea
                  className='Input-Field-TextArea'
                  placeholder={`${requestStatus === 'Approve' ?
                    'Add a comment...' :
                    requestStatus === 'Reassign' ?
                      'Add a comment for the new approver' :
                      'Provide a reason for rejecting this request'
                    }`}
                  value={changeStatusComment}
                  onChange={(e) => setChangeStatusComment(e.target.value)}
                ></textarea>
              </div>

            </div>

          </div>

          {/* FOOTER */}
          <div className='border-t-2 py-3 px-4 flex flex-row gap-3 justify-center'>

            <button className='Primary-Button w-[150px]' disabled={isSubmitDisabled()} onClick={() => handleSubmit()}>
              Proceed
            </button>

            {/* Cancel: Returns to previous screen, clears the Text field. */}
            <button className='Secondary-Button w-[150px]' onClick={() => {
              setRequestStatus('');
              setChangeStatusComment('');
              setStage('pre-selection');

              setSearchData({
                'newUserInput': '', // email of the selected user
                'newUserName': '', // fullname of the selected user
                'newGivenname': '', // givenname of the selected user
                'newSurname': '', // surname of the selected user
                'newJobTitle': '', // jobtitle of the selected user
                'searchResults': '',
                'queryMessage': '',
              });
            }}>
              Cancel
            </button>

          </div>

        </div>

      </div>

      {/* =========================================================== */}
      {/* POST-SELECTION STAGE: SHOW SUCCESS TOAST                    */}
      {/* =========================================================== */}
      <div className='fixed bottom-0 left-0 right-0 flex flex-col justify-between w-full h-[150px] rounded-lg bg-white shadow-lg z-10 p-3' hidden={stage !== 'post-selection'}>

        <div className='flex flex-row gap-2 p-3 bg-[#e7f2da] rounded-md'>
          <img src={TickIcon} alt='Success'></img>
          <label>Successfully updated approval.</label>
        </div>

        <div className='Submit-Button' onClick={() => setStage('pre-selection')}>
          Close
        </div>

      </div>

      {/* =========================================================== */}
      {/* ERROR: SHOW FAILURE TOAST                                   */}
      {/* =========================================================== */}
      <div className='fixed bottom-0 left-0 right-0 flex flex-col justify-between w-full h-[170px] rounded-lg bg-white shadow-lg z-10 p-3' hidden={stage !== 'error'}>

        <div className='flex flex-row gap-2 p-3 py-2 w-full bg-[#FFEBEE] rounded-md items-center justify-center '>
          <img className='w-[25px] h-[25px]' src={ErrorIcon} alt='Error'></img>
          <label className='font-[450]'>Something went wrong. Please try again or <Link className='underline' target='_blank' to='/help'>get help</Link>.</label>
        </div>

        <div className='Submit-Button' onClick={() => setStage('pre-selection')}>
          Close
        </div>

      </div>


    </>

  )
}
